import React, { useState, useRef } from "react";
import profileImage from "../../assets/user-solid.svg";
import { useNavigate } from "react-router-dom";
import { ThreeDots } from "react-loader-spinner";
import { API_BASE_URL } from "../../config";

const SalesRegistration = () => {
  const navigate = useNavigate(); // Hook for navigation
  const [fullName, setFullName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [education, setEducation] = useState("");
  const [customEducation, setCustomEducation] = useState("");
  const [photoSrc, setPhotoSrc] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);

  const [allowManualUpload, setAllowManualUpload] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleTakePhoto = async () => {
    try {
      if (!isCameraOpen) {
        setIsCameraOpen(true);
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } else {
        // Capture photo
        if (videoRef.current && canvasRef.current) {
          const context = canvasRef.current.getContext("2d");
          if (context) {
            const width = videoRef.current.videoWidth || 640;
            const height = videoRef.current.videoHeight || 480;
            canvasRef.current.width = width;
            canvasRef.current.height = height;
            context.drawImage(videoRef.current, 0, 0, width, height);
            const photoDataUrl = canvasRef.current.toDataURL("image/png");
            setPhotoSrc(photoDataUrl);

            // Upload the image after taking the photo
            uploadImage(photoDataUrl);
          } else {
            setErrorMessage(
              "Unable to access camera context. Please try the Upload Photo button below."
            );
            setAllowManualUpload(true);
          }
        } else {
          setErrorMessage(
            "Camera is not ready. Please try the Upload Photo button below."
          );
          setAllowManualUpload(true);
        }
        // Stop camera
        if (videoRef.current && videoRef.current.srcObject) {
          const tracks = (
            videoRef.current.srcObject as MediaStream
          ).getTracks();
          tracks.forEach((track) => track.stop());
          videoRef.current.srcObject = null;
        }
        setIsCameraOpen(false);
      }
    } catch (error) {
      console.error("Error accessing camera:", error);
      setErrorMessage(
        "Failed to access camera. Please ensure camera permissions are granted or use the Upload Photo button below."
      );
      setAllowManualUpload(true);
    }
  };

  const uploadImage = async (photoDataUrl: string) => {
    try {
      setIsSubmitting(true);
      setErrorMessage("");

      if (!photoDataUrl || !photoDataUrl.startsWith("data:image/")) {
        throw new Error("Failed to capture a valid image. Please try again.");
      }

      // Convert base64 to blob
      const response = await fetch(photoDataUrl);
      const blob = await response.blob();

      // Create form data
      const formData = new FormData();
      formData.append("image", blob, "profile-photo.png");

      // Upload image
      const uploadResponse = await fetch(
        `${API_BASE_URL}/upload/image`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload image");
      }

      const uploadResult = await uploadResponse.json();
      setImageUrl(uploadResult.imageUrl); // Assuming the response contains imageUrl property
      setSuccessMessage("Photo uploaded successfully!");
    } catch (error) {
      console.error("Error uploading image:", error);
      setErrorMessage("Failed to upload image. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const uploadFile = async (file: File) => {
    try {
      setIsSubmitting(true);
      setErrorMessage("");

      const formData = new FormData();
      formData.append("image", file, file.name || "profile-photo");

      const uploadResponse = await fetch(
        `${API_BASE_URL}/upload/image`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload image");
      }

      const uploadResult = await uploadResponse.json();
      setImageUrl(uploadResult.imageUrl);
      // Ensure preview shows using the uploaded image URL as a fallback
      if (!photoSrc && uploadResult.imageUrl) {
        setPhotoSrc(uploadResult.imageUrl);
      }
      setSuccessMessage("Photo uploaded successfully!");
    } catch (error) {
      console.error("Error uploading file:", error);
      setErrorMessage("Failed to upload image. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUploadButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const maxSizeBytes = 10 * 1024 * 1024; // 10MB
    const validMimeTypes = ["image/jpeg", "image/png"]; // jpg reports as image/jpeg
    const extension = file.name.split(".").pop()?.toLowerCase();
    const validExtensions = ["jpg", "jpeg", "png"];

    if (
      !validMimeTypes.includes(file.type) &&
      !validExtensions.includes(extension || "")
    ) {
      setErrorMessage("Only JPG, JPEG, or PNG images are allowed.");
      return;
    }

    if (file.size > maxSizeBytes) {
      setErrorMessage("Image must be 10MB or smaller.");
      return;
    }

    setErrorMessage("");

    // If camera is open, stop stream and hide video before previewing upload
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = (videoRef.current.srcObject as MediaStream).getTracks();
      tracks.forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
    setIsCameraOpen(false);

    // Preview
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setPhotoSrc(dataUrl);
    };
    reader.readAsDataURL(file);

    // Upload to backend
    await uploadFile(file);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!imageUrl) {
      setErrorMessage("Please take and upload a photo before registering.");
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage("");
      setSuccessMessage("");

      const registrationData = {
        name: fullName,
        phoneNo: phoneNumber,
        education: education === "Other" ? customEducation : education,
        image: imageUrl,
      };

      const response = await fetch(
        `${API_BASE_URL}/registration`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(registrationData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Registration failed");
      }

      // Get response data which should include the user ID
      const userData = await response.json();

      // Store the user ID in local storage
      if (userData && userData.data && userData.data.id) {
        localStorage.setItem("candidateId", userData.data.id);

        // Create warning count for the new candidate
        try {
          const warningResponse = await fetch(
            `${API_BASE_URL}/warning/create`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                studentId: userData.data.id,
                warningCount: "0",
              }),
            }
          );

          if (!warningResponse.ok) {
            console.warn(
              "Failed to create warning count, but registration was successful"
            );
          } else {
            console.log("Warning count created successfully");
            // Store warning ID in session
            try {
              const warningData = await warningResponse.json();
              if (warningData && warningData.id) {
                sessionStorage.setItem("warningId", warningData.id.toString());
                console.log("Warning ID stored in session:", warningData.id);
              } else {
                console.warn("Warning ID not found in response");
              }
            } catch (parseError) {
              console.warn("Error parsing warning response:", parseError);
            }
          }
        } catch (warningError) {
          console.warn("Error creating warning count:", warningError);
        }

        setSuccessMessage("Registration successful! Redirecting...");
      } else {
        console.warn("User ID not found in registration response");
      }

      // Navigate to memorization page after successful registration
      setTimeout(() => {
        navigate("/instructions");
      }, 1500);
    } catch (error) {
      console.error("Error registering:", error);
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Registration failed. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const educationOptions = ["ANM", "GNM", "BSc (Nursing)", "Other"];

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-200 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-red-400 text-white text-center py-6 px-4">
          <h1 className="text-2xl font-bold sm:text-3xl">Welcome</h1>
          <p className="text-sm mt-1 opacity-90 sm:text-base">
            Please fill your information
          </p>
        </div>

        <form onSubmit={handleRegister} className="p-6 sm:p-8">
          {/* Error and Success Messages */}
          {errorMessage && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
              {errorMessage}
            </div>
          )}
          {successMessage && (
            <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg">
              {successMessage}
            </div>
          )}

          {/* Profile Photo */}
          <div className="mb-6 text-center">
            <p className="text-gray-700 mb-2 font-medium">Profile Photo</p>

            <div className="relative mx-auto mb-4">
              {isCameraOpen && !photoSrc ? (
                <div className="relative">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-32 h-32 rounded-full object-cover mx-auto border-2 border-red-500"
                  />
                </div>
              ) : (
                <div className="w-32 h-32 rounded-full bg-gray-200 mx-auto flex items-center justify-center overflow-hidden">
                  {photoSrc ? (
                    <img
                      src={photoSrc}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <img src={profileImage} alt="icon" className="w-20 h-20" />
                  )}
                </div>
              )}
              <canvas ref={canvasRef} className="hidden" />
            </div>

            <button
              type="button"
              onClick={handleTakePhoto}
              disabled={isSubmitting}
              className="bg-red-500 text-white py-2 px-4 rounded-lg hover:bg-red-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting && !isCameraOpen ? (
                <ThreeDots
                  height="24"
                  width="24"
                  color="#ffffff"
                  ariaLabel="loading"
                  wrapperClass="inline-block"
                />
              ) : isCameraOpen ? (
                "Capture"
              ) : (
                "Take Photo"
              )}
            </button>

            {allowManualUpload && (
              <div className="mt-3 flex flex-col items-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png, image/jpeg"
                  className="hidden"
                  onChange={handleFileChange}
                />
                <button
                  type="button"
                  onClick={handleUploadButtonClick}
                  disabled={isSubmitting}
                  className="mt-2 bg-gray-100 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Upload Photo
                </button>
                <p className="text-xs text-gray-500 mt-1">
                  JPG, JPEG, PNG • Max 10MB
                </p>
              </div>
            )}

            {imageUrl && (
              <p className="text-sm text-green-600 mt-2">
                Image uploaded successfully
              </p>
            )}
          </div>

          {/* Full Name */}
          <div className="mb-4">
            <label
              htmlFor="fullName"
              className="block text-gray-700 mb-2 font-medium"
            >
              Full Name
            </label>
            <input
              type="text"
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Enter your full name"
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-500 transition"
              required
              disabled={isSubmitting}
            />
          </div>

          {/* Phone Number */}
          <div className="mb-4">
            <label
              htmlFor="phoneNumber"
              className="block text-gray-700 mb-2 font-medium"
            >
              Phone Number
            </label>
            <input
              type="tel"
              id="phoneNumber"
              value={phoneNumber}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                if (value.length <= 10) {
                  setPhoneNumber(value);
                }
              }}
              placeholder="Enter your phone number"
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-500 transition"
              required
              maxLength={10}
              pattern="[0-9]{10}"
              title="Please enter a valid 10-digit phone number"
              disabled={isSubmitting}
            />
          </div>

          {/* Education */}
          <div className="mb-6">
            <label
              htmlFor="education"
              className="block text-gray-700 mb-2 font-medium"
            >
              Education
            </label>
            <select
              id="education"
              value={education}
              onChange={(e) => setEducation(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-500 appearance-none bg-white"
              required
              disabled={isSubmitting}
            >
              <option value="" disabled>
                Select your education
              </option>
              {educationOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {education === "Other" && (
              <input
                type="text"
                value={customEducation}
                onChange={(e) => setCustomEducation(e.target.value)}
                placeholder="Please specify your education"
                className="w-full border border-gray-300 rounded-lg px-4 py-3 mt-2 focus:outline-none focus:ring-2 focus:ring-red-500 transition"
                required
                disabled={isSubmitting}
              />
            )}
          </div>

          {/* Register Button */}
          <button
            type="submit"
            disabled={isSubmitting || !imageUrl}
            className="w-full bg-gradient-to-r from-red-500 to-red-400 text-white py-3 rounded-lg hover:from-red-600 hover:to-red-500 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isSubmitting ? (
              <ThreeDots
                height="24"
                width="50"
                color="#ffffff"
                ariaLabel="loading"
                wrapperClass="flex justify-center items-center"
              />
            ) : (
              "Register"
            )}
          </button>
        </form>

        {/* Copyright Footer */}
        <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 text-center">
          <p className="text-sm text-gray-600">
            © NAVIS HR 2025. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SalesRegistration;
